/**
 * Trust-Gated Actions V1
 * 
 * Provides UI-only gating logic based on Trust Flow status to safely
 * enable/disable actions without introducing execution authority.
 * 
 * BOUNDARIES:
 * - UI-only gating: does not change execution authority
 * - Does not bypass MoveSelectionReceipt
 * - Fail-closed default: actions disabled by default
 * - No mutation of inputs
 * 
 * INVARIANT:
 * Trust-gated actions provide UI gating only. They do not grant execution
 * or approval authority and do not bypass existing governance checks.
 */

export type TrustFlowStatus = 'verified' | 'partially_verified' | 'invalid';

export type ActionType = 'export' | 'copy' | 'share';

/**
 * Check if an action is allowed based on trust flow status
 * 
 * Rules:
 * - verified → allow all non-execution actions (export, copy, share)
 * - partially_verified → allow limited actions (copy only)
 * - invalid → disable all actions
 * 
 * Fail-closed: default to false (action disabled)
 */
export function isActionAllowed(
  trustFlowStatus: TrustFlowStatus,
  actionType: ActionType
): boolean {
  switch (trustFlowStatus) {
    case 'verified':
      // Full chain intact: allow all non-execution actions
      return true;
    
    case 'partially_verified':
      // Safe but incomplete: allow only copy
      return actionType === 'copy';
    
    case 'invalid':
      // Broken or inconsistent: disable all actions
      return false;
    
    default:
      // Fail-closed: disable actions
      return false;
  }
}

/**
 * Get disabled reason for an action based on trust flow status
 * 
 * Returns a user-friendly message explaining why an action is disabled.
 */
export function getDisabledReason(
  trustFlowStatus: TrustFlowStatus,
  actionType: ActionType
): string | null {
  if (isActionAllowed(trustFlowStatus, actionType)) {
    return null;
  }

  switch (trustFlowStatus) {
    case 'partially_verified':
      if (actionType === 'export' || actionType === 'share') {
        return 'Export and share actions require full chain verification';
      }
      return null;
    
    case 'invalid':
      return 'Cannot verify process continuity';
    
    default:
      return 'Action disabled';
  }
}

/**
 * Get button disabled state based on trust flow status
 * 
 * Returns true if the button should be disabled, false otherwise.
 */
export function isButtonDisabled(
  trustFlowStatus: TrustFlowStatus,
  actionType: ActionType
): boolean {
  return !isActionAllowed(trustFlowStatus, actionType);
}

/**
 * Safety verification: ensure trust-gated actions do not bypass execution authority
 * 
 * This function always returns true, confirming that trust-gated actions
 * do not grant execution authority. It exists for documentation and testing purposes.
 */
export function verifyNoExecutionAuthority(): boolean {
  return true;
}

/**
 * Safety verification: ensure trust-gated actions do not bypass MoveSelectionReceipt
 * 
 * This function always returns true, confirming that trust-gated actions
 * do not bypass MoveSelectionReceipt. It exists for documentation and testing purposes.
 */
export function verifyDoesNotBypassMoveSelectionReceipt(): boolean {
  return true;
}
